import React, { useEffect } from 'react';
import { Container } from '@montana/shared/container';
import { useCheckout } from '@montana/containers/checkout';
import { ItemSummary } from '../item-summary';
import { ConfirmationSteps } from './confirmation-steps';
import { InformationSummaries } from './information-summaries';
import { usePage } from '@montana/containers/page';
import { Translate } from '@montana/shared/translate';
import { useTracking } from '@montana/containers/tracking';
import { IGtmProductWithQuantity, IGtmPurchaseActionField } from '@montana/services/tracking/tracking';
import { IConfirmationPage } from '@montana/models/generated/cms.interface';

export const ConfirmationPage = ({ text }: IConfirmationPage) => {

    const { orderInformation } = useCheckout();
    const { setPageHasHero } = usePage();
    const { trackCheckoutOption, trackPurchase } = useTracking();

    useEffect(() => {
        setPageHasHero(true);
    }, []);

    useEffect(() => {
        // We track the order as final, when it is present, hasn't failed and hasn't been fetched before.
        if (orderInformation && !orderInformation?.hasPaymentFailed && !orderInformation.fetchedTime) {
            // Track Checkout Option
            trackCheckoutOption(3, orderInformation.paymentType || '');

            // Track Purchase / Transaction
            const gtmProducts: IGtmProductWithQuantity[] = orderInformation?.lines?.length ? orderInformation.lines.map(line => ({
                id: line.code || '',
                name: line.name || '',
                price: line.price?.amount || 0,
                quantity: line.quantity,
                category: line.category?.categoryName || '',
                ean: line.ean || '',
            })) : [];

            const actionField: IGtmPurchaseActionField = {
                id: orderInformation.orderId || '',
                revenue: orderInformation?.totalPrice?.amount || 0,
                tax: orderInformation?.totalVatAmount?.amount || 0,
                shipping: orderInformation?.deliveryPrice?.amount || 0,
                affiliation: '',
                coupon: orderInformation?.discounts?.map(discount => discount.discountName).join(' | ') || '',
            };

            trackPurchase(orderInformation?.totalPrice?.currency || '', gtmProducts, actionField);
        }
    }, [orderInformation]);

    return (
        <div className="confirmation-page">
            <section className="confirmation-page__section confirmation-page__top-banner">
                <div className="confirmation-page__top-banner__bg"></div>
                <div className="confirmation-page__top-banner__content">
                    <div className="confirmation-page__column">
                        <span>
                            <span className="confirmation-page__title">
                                <Translate value="checkoutPage.confirmationPageTitle" defaultValue="[Thank you for shopping at Montana]" />
                            </span>
                            <span className="confirmation-page__subtitle"> — </span>
                            <span className="confirmation-page__subtitle">
                                <Translate value="checkoutPage.confirmationPageSubTitle" defaultValue="[We are now in the proces of build your unique Montana furniture.]" />
                            </span>
                        </span>
                    </div>
                    <div className="confirmation-page__column confirmation-page__description">
                        {!!text && (
                            <span dangerouslySetInnerHTML={{ __html: text.replace('{user-email}', orderInformation?.personalInformation?.email || 'email') }}></span>
                        )}
                    </div>
                </div>
            </section>
            <Container>
                <section className="confirmation-page__section">
                    <div className="confirmation-page__column">
                        <h5 className="confirmation-heading">
                            <Translate
                                value="checkoutPage.confirmationPageYourOrderDetails"
                                defaultValue="[Your order details]"
                            />
                        </h5>
                        <div className="confirmation-page__row">
                            <section className="confirmation-page__personal-information">
                                <InformationSummaries
                                    personalInfo={orderInformation?.personalInformation}
                                    pickUpAddress={orderInformation?.pickupLocation}
                                    expectedDelivery={orderInformation?.dispatchTime}
                                />
                            </section>
                            <section className="confirmation-page__item-summary">
                                <div className="information-summaries-heading">
                                    <Translate value="checkoutPage.confirmationOrderHeading" defaultValue="[Order]" />
                                </div>
                                <ItemSummary
                                    lines={orderInformation?.lines}
                                    totalPrice={orderInformation?.totalPrice}
                                    totalVatAmount={orderInformation?.totalVatAmount}
                                    dispatchTime={orderInformation?.dispatchTime}
                                    deliveryPrice={orderInformation?.deliveryPrice}
                                    showShippingCost={true}
                                />
                            </section>
                        </div>
                    </div>
                    <div className="confirmation-page__column">
                        <h5 className="confirmation-heading display-5">
                            <Translate
                                value="checkoutPage.confirmationPageStepsHeading"
                                defaultValue="[What's going to happen now?]"
                            />
                        </h5>
                        <ConfirmationSteps />
                    </div>
                </section>
            </Container>
        </div>
    );
};
